<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import MessageApi from 'dashboard/api/inbox/message';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: Number,
    required: true,
  },
  priorityName: {
    type: String,
    required: true,
  },
  priorityKey: {
    type: [String, null],
    default: null,
  },
});

const emit = defineEmits(['close', 'confirm']);

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();

const logMessage = ref('');
const isSubmitting = ref(false);

const modalTitle = computed(() => t('PRIORITY_SETTINGS.LOG_MODAL.TITLE'));

const modalDescription = computed(() => 
  t('PRIORITY_SETTINGS.LOG_MODAL.DESCRIPTION', { priority: props.priorityName })
);

// Get presets from account settings
const presets = computed(() => {
  const settings = currentAccount.value?.settings || {};
  return settings.priority_log_presets || [];
});

// Add preset text to the message
const addPreset = (presetText) => {
  if (logMessage.value.trim()) {
    // Add with a space/newline separator if there's existing text
    logMessage.value = logMessage.value.trim() + '\n' + presetText;
  } else {
    logMessage.value = presetText;
  }
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  
  try {
    // If there's a log message, create a private note
    if (logMessage.value.trim()) {
      const noteContent = `${logMessage.value}\n\nPriority changed to **${props.priorityName}**`;
      
      await MessageApi.create({
        conversationId: props.conversationId,
        message: noteContent,
        private: true,
      });
    }
    
    // Emit confirm to proceed with priority change
    emit('confirm', props.priorityKey);
    handleClose();
  } catch (error) {
    useAlert(t('PRIORITY_SETTINGS.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSkip = () => {
  // Proceed without logging
  emit('confirm', props.priorityKey);
  handleClose();
};

const handleClose = () => {
  logMessage.value = '';
  emit('close');
};
</script>

<template>
  <Modal
    :show="show"
    :on-close="handleClose"
    size="medium"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <header class="py-4 px-8">
        <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
          {{ modalTitle }}
        </h2>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ modalDescription }}
        </p>
      </header>

      <div class="px-8 pb-4">
        <TextArea
          v-model="logMessage"
          :placeholder="$t('PRIORITY_SETTINGS.LOG_MODAL.PLACEHOLDER')"
          rows="4"
          class="w-full"
        />
        
        <!-- Preset chips -->
        <div v-if="presets.length > 0" class="mt-3">
          <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
            {{ $t('PRIORITY_SETTINGS.LOG_MODAL.QUICK_ADD') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="(preset, index) in presets"
              :key="index"
              type="button"
              class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full 
                     bg-slate-100 dark:bg-slate-700 
                     text-slate-700 dark:text-slate-200
                     hover:bg-slate-200 dark:hover:bg-slate-600
                     border border-slate-200 dark:border-slate-600
                     transition-colors duration-150 ease-in-out
                     focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-woot-500"
              @click="addPreset(preset)"
            >
              <fluent-icon icon="add" size="14" class="mr-1.5 -ml-0.5" />
              {{ preset }}
            </button>
          </div>
        </div>
      </div>

      <footer class="flex items-center justify-between gap-2 py-4 px-8 bg-slate-50 dark:bg-slate-800">
        <NextButton
          slate
          faded
          :label="$t('PRIORITY_SETTINGS.LOG_MODAL.SKIP')"
          @click="handleSkip"
        />
        <div class="flex gap-2">
          <NextButton
            slate
            :label="$t('PRIORITY_SETTINGS.LOG_MODAL.CANCEL')"
            @click="handleClose"
          />
          <NextButton
            blue
            :is-loading="isSubmitting"
            :label="$t('PRIORITY_SETTINGS.LOG_MODAL.SUBMIT')"
            @click="handleSubmit"
          />
        </div>
      </footer>
    </div>
  </Modal>
</template>
